import React, { useEffect, useState } from 'react';
// Use specific named imports to avoid type issues
import { Chart, registerables, Colors } from 'chart.js';
import { Bar, Line, Pie, Scatter, Doughnut } from 'react-chartjs-2';
// We need to import RadarController separately to register it properly
import { RadarController } from 'chart.js';
import './VisualizationRenderer.css';
import { 
  shouldRenderChart, 
  getPerformanceProfile, 
  analyzeChartComplexity,
  hasEnoughMemoryForChart 
} from '../../utils/chartPerformanceUtils';

// Register ChartJS components including the Colors plugin
Chart.register(...registerables, RadarController, Colors);

// Define the structure of visualization data
export interface VisualizationData {
  type: 'bar' | 'line' | 'pie' | 'scatter' | 'doughnut' | 'radar' | 'bubble' | 'polarArea' | 'histogram' | 'horizontalBar' | 'horizontalbar' | 'area';
  title: string;
  description: string;
  labels: string[];
  datasets: {
    label: string;
    data: number[] | Array<{x: number, y: number}> | Array<{x: number, y: number, r: number}>; // Allow bubble chart coordinates
    backgroundColor?: string | string[];
    borderColor?: string | string[];
    borderWidth?: number;
    pointBackgroundColor?: string | string[];
    fill?: boolean;
    radius?: number; // For bubble charts
    pointRadius?: number; // For scatter and line charts
    tension?: number; // For line charts
  }[];
  xAxisLabel?: string;
  yAxisLabel?: string;
  sectionId?: number; // The index of the text section this visualization is associated with
  // For histogram-specific data
  bins?: number; // Number of bins for histogram
  binSize?: number; // Size of each bin for histogram
}

interface VisualizationRendererProps {
  data: VisualizationData;
  halfSize?: boolean;
  disableInteraction?: boolean;
}

// Chart.js brand colors palette (7 colors that cycle)
// These are the default colors from the Chart.js Colors plugin
function generateColors(count: number, opacity: number = 0.7): string[] {
  const chartJsBrandColors = [
    `rgba(54, 162, 235, ${opacity})`,   // Blue
    `rgba(255, 99, 132, ${opacity})`,   // Pink/Red
    `rgba(255, 205, 86, ${opacity})`,   // Yellow
    `rgba(75, 192, 192, ${opacity})`,   // Teal
    `rgba(153, 102, 255, ${opacity})`,  // Purple
    `rgba(255, 159, 64, ${opacity})`,   // Orange
    `rgba(201, 203, 207, ${opacity})`   // Grey
  ];
  
  if (count <= chartJsBrandColors.length) {
    return chartJsBrandColors.slice(0, count);
  }
  
  // If we need more colors than our base set, cycle through the colors
  const colors = [];
  for (let i = 0; i < count; i++) {
    colors.push(chartJsBrandColors[i % chartJsBrandColors.length]);
  }
  
  return colors;
}

// Custom chart components since react-chartjs-2 might not export all types directly
const Radar = (props: any) => {
  const { data, options, redraw } = props;
  const chartRef = React.useRef<HTMLCanvasElement>(null);
  
  React.useEffect(() => {
    if (!chartRef.current) return;
    
    // Destroy previous chart instance if it exists
    const prevChart = Chart.getChart(chartRef.current);
    if (prevChart) {
      prevChart.destroy();
    }
    
    // Create new chart
    new Chart(chartRef.current, {
      type: 'radar',
      data,
      options: {
        ...options,
        scales: {
          r: {
            beginAtZero: true
          }
        }
      }
    });
  }, [data, options, redraw]);
  
  return <canvas ref={chartRef} />;
};

const Bubble = (props: any) => {
  const { data, options, redraw } = props;
  const chartRef = React.useRef<HTMLCanvasElement>(null);
  
  React.useEffect(() => {
    if (!chartRef.current) return;
    
    const prevChart = Chart.getChart(chartRef.current);
    if (prevChart) {
      prevChart.destroy();
    }
    
    new Chart(chartRef.current, {
      type: 'bubble',
      data,
      options
    });
  }, [data, options, redraw]);
  
  return <canvas ref={chartRef} />;
};

const PolarArea = (props: any) => {
  const { data, options, redraw } = props;
  const chartRef = React.useRef<HTMLCanvasElement>(null);
  
  React.useEffect(() => {
    if (!chartRef.current) return;
    
    const prevChart = Chart.getChart(chartRef.current);
    if (prevChart) {
      prevChart.destroy();
    }
    
    new Chart(chartRef.current, {
      type: 'polarArea',
      data,
      options
    });
  }, [data, options, redraw]);
  
  return <canvas ref={chartRef} />;
};

// Function to process data for histogram
const processHistogramData = (data: VisualizationData) => {
  // For histogram, we need to convert raw data into bins
  if (!data.datasets[0] || !Array.isArray(data.datasets[0].data)) {
    return data;
  }
  
  const rawData = data.datasets[0].data as number[];
  const bins = data.bins || 10;
  
  // Calculate bin range
  const min = Math.min(...rawData);
  const max = Math.max(...rawData);
  const binSize = data.binSize || (max - min) / bins;
  
  // Create bins
  const binCounts: number[] = new Array(bins).fill(0);
  const binLabels: string[] = [];
  
  // Generate bin labels
  for (let i = 0; i < bins; i++) {
    const binStart = min + i * binSize;
    const binEnd = min + (i + 1) * binSize;
    binLabels.push(`${binStart.toFixed(1)}-${binEnd.toFixed(1)}`);
  }
  
  // Count data points in each bin
  rawData.forEach(value => {
    const binIndex = Math.min(Math.floor((value - min) / binSize), bins - 1);
    binCounts[binIndex]++;
  });
  
  return {
    ...data,
    type: 'bar' as const, // Histogram is rendered as a bar chart
    labels: binLabels,
    datasets: [{
      ...data.datasets[0],
      data: binCounts,
      backgroundColor: data.datasets[0].backgroundColor // Let Colors plugin handle if undefined
    }]
  };
};

const VisualizationRenderer: React.FC<VisualizationRendererProps> = ({ data, halfSize = false, disableInteraction = false }) => {
  const [renderError, setRenderError] = useState<string | null>(null);
  const [isReady, setIsReady] = useState<boolean>(false);
  const [shouldHide, setShouldHide] = useState<boolean>(false);
  const [performanceInfo, setPerformanceInfo] = useState<string | null>(null);

  // Validate the data before rendering
  useEffect(() => {
    try {
      // Basic validation
      if (!data) {
        setRenderError('No visualization data provided');
        return;
      }

      // Performance-based rendering decision
      if (!shouldRenderChart(data)) {
        const complexity = analyzeChartComplexity(data);
        const performance = getPerformanceProfile();
        
        console.warn(`Hiding chart due to performance constraints:`, {
          chartType: data.type,
          dataPoints: complexity.dataPointCount,
          isLowSpec: performance.isLowSpec,
          hasLimitedMemory: performance.hasLimitedMemory
        });
        
        setShouldHide(true);
        setPerformanceInfo(`Chart hidden (${complexity.dataPointCount} data points) for better performance on this device`);
        return;
      }

      // Memory check
      if (!hasEnoughMemoryForChart(data)) {
        console.warn('Hiding chart due to insufficient memory');
        setShouldHide(true);
        setPerformanceInfo('Chart hidden due to memory constraints on this device');
        return;
      }
      
      // Normalize chart type for case-insensitive comparison
      if (data.type) {
        const normalizedType = data.type.toLowerCase();
        
        // Check for horizontalBar/horizontalbar and normalize to bar with options
        if (normalizedType === 'horizontalbar') {
          console.log('Converting horizontalBar to bar with horizontal orientation');
          data.type = 'bar' as any; // We'll handle the horizontal orientation in options
        }
      }
      
      if (!data.type || !['bar', 'line', 'pie', 'scatter', 'doughnut', 'radar', 'bubble', 'polarArea', 'histogram', 'area'].includes(data.type)) {
        setRenderError(`Invalid chart type: ${data.type || 'undefined'}`);
        return;
      }
      
      // Labels validation - more flexible for scatter plots and histograms
      if (data.type === 'scatter' || data.type === 'histogram') {
        // For scatter plots and histograms, labels can be empty or auto-generated
        if (!data.labels || !Array.isArray(data.labels)) {
          data.labels = [];
        }
      } else {
        // For other chart types, labels are required
        if (!data.labels || !Array.isArray(data.labels) || data.labels.length === 0) {
          setRenderError('Invalid or empty labels array');
          return;
        }
      }
      
      if (!data.datasets || !Array.isArray(data.datasets) || data.datasets.length === 0) {
        setRenderError('Invalid or empty datasets array');
        return;
      }
      
      // Check if all datasets have data arrays
      const invalidDatasets = data.datasets.filter(ds => 
        !ds.data || !Array.isArray(ds.data) || ds.data.length === 0
      );
      
      if (invalidDatasets.length > 0) {
        setRenderError(`${invalidDatasets.length} datasets have invalid or empty data arrays`);
        return;
      }
      
      // Chart-specific validation
      if (['pie', 'doughnut', 'polarArea'].includes(data.type)) {
        // Pie, doughnut, and polar area charts can only have one dataset
        if (data.datasets.length > 1) {
          console.warn(`${data.type} chart should have only one dataset, using first dataset`);
        }
        
        // Number of data points must match number of labels
        const firstDataset = data.datasets[0];
        if (firstDataset.data.length !== data.labels.length) {
          setRenderError(`${data.type} chart data points (${firstDataset.data.length}) don't match labels count (${data.labels.length})`);
          return;
        }
      }
      
      // For area charts, ensure fill is set to true
      if (data.type === 'area') {
        for (const dataset of data.datasets) {
          if (dataset.fill === undefined) {
            dataset.fill = true;
          }
        }
      }
      
      // Specific handling for radar charts
      if (data.type === 'radar') {
        for (const dataset of data.datasets) {
          // Set default fill for radar charts
          if (dataset.fill === undefined) {
            dataset.fill = true;
          }
          // Set default border width if not specified
          if (!dataset.borderWidth) {
            dataset.borderWidth = 2;
          }
        }
      }
      
      // Colors plugin will automatically handle backgroundColor for bar charts
      
      // All validation passed
      setIsReady(true);
      setRenderError(null);
      
    } catch (err) {
      console.error('Error validating visualization data:', err);
      setRenderError(`Error validating data: ${err instanceof Error ? err.message : String(err)}`);
    }
  }, [data]);
  
  // Reference to chart container for ensuring proper dimensions
  const renderRef = React.useRef<HTMLDivElement>(null);
  const chartInstanceRef = React.useRef<Chart | null>(null);
  
  // Prepare chart data and options
  const prepareChartData = () => {
    if (!data || !data.datasets) {
      return {
        labels: [],
        datasets: []
      };
    }

    // For scatter plots and histograms, labels array can be empty since they are auto-generated
    const labelsToUse = (data.type === 'scatter' || data.type === 'histogram') ? (data.labels || []) : data.labels;

    return {
      labels: labelsToUse,
      datasets: data.datasets.map((dataset, index) => {
        // Let the Colors plugin handle color assignment automatically
        // Only set colors if they're explicitly provided in the dataset
        return {
          ...dataset,
          backgroundColor: dataset.backgroundColor, // Let Colors plugin handle if undefined
          borderColor: dataset.borderColor, // Let Colors plugin handle if undefined
          borderWidth: dataset.borderWidth || 1,
        };
      }),
    };
  };

  const chartData = prepareChartData();

  // Prepare chart data for processed data (like histogram)
  const prepareChartDataForProcessed = (processedData: VisualizationData) => {
    if (!processedData || !processedData.datasets) {
      return {
        labels: [],
        datasets: []
      };
    }

    return {
      labels: processedData.labels,
      datasets: processedData.datasets.map((dataset, index) => {
        // Let the Colors plugin handle color assignment automatically
        // Only set colors if they're explicitly provided in the dataset
        return {
          ...dataset,
          backgroundColor: dataset.backgroundColor, // Let Colors plugin handle if undefined
          borderColor: dataset.borderColor, // Let Colors plugin handle if undefined
          borderWidth: dataset.borderWidth || 1,
        };
      }),
    };
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    // Add indexAxis option to support horizontal bar charts
    indexAxis: (data?.type === 'horizontalBar' || data?.type === 'horizontalbar') ? 'y' as const : 'x' as const,
    // Disable all interactions when disableInteraction is true
    interaction: {
      intersect: !disableInteraction,
      mode: disableInteraction ? false as any : 'nearest' as const
    },
    onHover: disableInteraction ? undefined : undefined,
    onClick: disableInteraction ? undefined : undefined,
    plugins: {
      // Enable the Colors plugin for automatic color assignment
      colors: {
        enabled: true,
        forceOverride: true
      },
      legend: {
        position: 'top' as const,
        onClick: disableInteraction ? undefined : undefined,
        onHover: disableInteraction ? undefined : undefined,
        labels: {
          font: {
            family: "'Segoe UI', -apple-system, BlinkMacSystemFont, Roboto, sans-serif",
            size: halfSize ? 12 : 14
          },
          padding: halfSize ? 12 : 18,
          usePointStyle: true,
          pointStyle: 'circle'
        }
      },
      title: {
        display: true,
        text: data?.title || 'Chart',
        font: {
          family: "'Segoe UI', -apple-system, BlinkMacSystemFont, Roboto, sans-serif",
          size: halfSize ? 18 : 20,
          weight: '600'
        },
        padding: {
          top: halfSize ? 10 : 14,
          bottom: halfSize ? 18 : 24
        },
        color: '#2a4065'
      },
      tooltip: disableInteraction ? {
        enabled: false
      } : {
        backgroundColor: 'rgba(42, 64, 101, 0.8)',
        titleFont: {
          family: "'Segoe UI', -apple-system, BlinkMacSystemFont, Roboto, sans-serif",
          size: halfSize ? 12 : 14,
          weight: '500'
        },
        bodyFont: {
          family: "'Segoe UI', -apple-system, BlinkMacSystemFont, Roboto, sans-serif",
          size: halfSize ? 11 : 13
        },
        padding: halfSize ? 10 : 12,
        cornerRadius: 6,
        callbacks: {
          label: function(context: any) {
            let label = context.dataset.label || '';
            if (label) {
              label += ': ';
            }
            if (context.parsed.y !== null) {
              // Format numbers with commas and 2 decimal places if needed
              label += new Intl.NumberFormat('en-US', {
                minimumFractionDigits: 0,
                maximumFractionDigits: 2,
              }).format(context.parsed.y);
            }
            return label;
          }
        }
      }
    },
    scales: {
      x: {
        title: {
          display: !!data?.xAxisLabel,
          text: data?.xAxisLabel || '',
          font: {
            family: "'Segoe UI', -apple-system, BlinkMacSystemFont, Roboto, sans-serif",
            size: halfSize ? 14 : 16,
          },
          color: '#333'
        },
        grid: {
          color: 'rgba(0, 0, 0, 0.05)',
          drawBorder: false
        },
        ticks: {
          font: {
            family: "'Segoe UI', -apple-system, BlinkMacSystemFont, Roboto, sans-serif",
            size: halfSize ? 12 : 14
          },
          color: '#555',
          maxRotation: 45,
          minRotation: 0
        }
      },
      y: {
        title: {
          display: !!data?.yAxisLabel,
          text: data?.yAxisLabel || '',
          font: {
            family: "'Segoe UI', -apple-system, BlinkMacSystemFont, Roboto, sans-serif",
            size: halfSize ? 14 : 16,
          },
          color: '#333'
        },
        grid: {
          color: 'rgba(0, 0, 0, 0.05)',
          drawBorder: false
        },
        ticks: {
          font: {
            family: "'Segoe UI', -apple-system, BlinkMacSystemFont, Roboto, sans-serif",
            size: halfSize ? 12 : 14
          },
          color: '#555',
          callback: function(value: any) {
            // Format y-axis ticks with commas for thousands
            return new Intl.NumberFormat('en-US', {
              minimumFractionDigits: 0,
              maximumFractionDigits: 0,
            }).format(value);
          }
        }
      },
    },
    // Add animation config that works better with PDF export
    animation: {
      duration: 0 // Disable animations for PDF export reliability
    },
    elements: {
      line: {
        tension: 0.4 // Smooth lines slightly
      },
      point: {
        radius: 4,
        hitRadius: disableInteraction ? 0 : 8,
        hoverRadius: disableInteraction ? 4 : 6
      },
      bar: {
        hoverBackgroundColor: disableInteraction ? undefined : undefined,
        hoverBorderColor: disableInteraction ? undefined : undefined
      },
      arc: {
        hoverBackgroundColor: disableInteraction ? undefined : undefined,
        hoverBorderColor: disableInteraction ? undefined : undefined
      }
    },
    layout: {
      padding: {
        left: 10,
        right: 10,
        top: 0,
        bottom: 10
      }
    },
    // Add explicit background color settings
    backgroundColor: 'white',
    borderColor: 'rgba(0, 0, 0, 0.1)',
    // Ensure proper rendering in PDF
    devicePixelRatio: 2
  };

  // Render appropriate chart based on type
  const renderChart = () => {
    if (!isReady) {
      return (
        <div className="chart-loading" style={{ 
          display: 'flex', 
          justifyContent: 'center', 
          alignItems: 'center', 
          height: '300px',
          backgroundColor: '#f8f9fa',
          borderRadius: '8px',
          border: '2px dashed #dee2e6'
        }}>
          <div style={{ textAlign: 'center', color: '#6c757d' }}>
            <div style={{ 
              width: '40px', 
              height: '40px', 
              border: '4px solid #e9ecef', 
              borderTop: '4px solid #007bff', 
              borderRadius: '50%', 
              animation: 'spin 1s linear infinite',
              margin: '0 auto 10px'
            }}></div>
            <p>Loading chart...</p>
            <small>This may take longer on slower devices</small>
          </div>
        </div>
      );
    }
    
    if (renderError) {
      return (
        <div className="chart-error" style={{
          padding: '20px',
          backgroundColor: '#f8d7da',
          border: '1px solid #f5c6cb',
          borderRadius: '8px',
          color: '#721c24'
        }}>
          <h4>Chart Rendering Error</h4>
          <p>Error: {renderError}</p>
          <details style={{ marginTop: '10px' }}>
            <summary style={{ cursor: 'pointer', fontWeight: 'bold' }}>Show Chart Data</summary>
            <pre style={{ 
              backgroundColor: '#fff', 
              padding: '10px', 
              borderRadius: '4px', 
              overflow: 'auto',
              fontSize: '12px',
              marginTop: '10px'
            }}>
              {data ? JSON.stringify(data, null, 2) : 'No data available'}
            </pre>
          </details>
          <p style={{ marginTop: '10px', fontSize: '14px' }}>
            <strong>Troubleshooting:</strong> Try refreshing the page or using a device with more available memory.
          </p>
        </div>
      );
    }
    
    try {
      // Process histogram data if needed
      let processedData = data;
      if (data.type === 'histogram') {
        processedData = processHistogramData(data);
      }
      
      // For horizontalBar, we've already normalized to 'bar' type with indexAxis: 'y'
      const isHorizontalBar = data.type === 'horizontalBar' || data.type === 'horizontalbar';
      
      // Prepare chart data based on processed data
      const finalChartData = data.type === 'histogram' ? 
        prepareChartDataForProcessed(processedData as VisualizationData) : 
        chartData;
      
      switch (data.type === 'histogram' ? 'bar' : data.type) {
        case 'bar':
          return <Bar data={finalChartData} options={{
            ...chartOptions,
            // If it was originally horizontalBar, set indexAxis to 'y'
            indexAxis: isHorizontalBar ? 'y' as const : 'x' as const
          }} redraw={true} />;
        case 'line':
          return <Line data={finalChartData} options={chartOptions} redraw={true} />;
        case 'area':
          // Area chart is just a line chart with fill enabled
          return <Line data={finalChartData} options={chartOptions} redraw={true} />;
        case 'pie':
          return <Pie data={finalChartData} options={chartOptions} redraw={true} />;
        case 'scatter':
          return <Scatter data={finalChartData} options={chartOptions} redraw={true} />;
        case 'doughnut':
          return <Doughnut data={finalChartData} options={chartOptions} redraw={true} />;
        case 'radar':
          return <Radar data={finalChartData} options={chartOptions} redraw={true} />;
        case 'bubble':
          return <Bubble data={finalChartData} options={chartOptions} redraw={true} />;
        case 'polarArea':
          return <PolarArea data={finalChartData} options={chartOptions} redraw={true} />;
        default:
          return (
            <div className="chart-error">
              <p>Unknown chart type: {data.type}</p>
            </div>
          );
      }
    } catch (error) {
      console.error("Error rendering chart:", error);
      return (
        <div className="chart-error">
          <p>Error rendering chart: {error instanceof Error ? error.message : String(error)}</p>
        </div>
      );
    }
  };

  // Determine container classes based on halfSize prop
  const containerClassName = `visualization-container${halfSize ? ' half-size' : ''}`;
  const chartHeight = halfSize ? '380px' : '450px';

  // Ensure charts have proper dimensions for PDF export - optimized for slow machines
  useEffect(() => {
    let attemptCount = 0;
    const maxAttempts = 10;
    let rafId: number;
    
    const fixCanvasDimensions = () => {
      if (!renderRef.current || attemptCount >= maxAttempts) return;
      
      // Find the canvas element
      const canvas = renderRef.current.querySelector('canvas');
      if (!canvas) {
        attemptCount++;
        // Use requestAnimationFrame for better performance on slow machines
        rafId = requestAnimationFrame(() => {
          setTimeout(fixCanvasDimensions, 200 * attemptCount); // Exponential backoff
        });
        return;
      }
      
      // Check if canvas has zero dimensions
      if (canvas.width === 0 || canvas.height === 0) {
        // Set explicit dimensions
        canvas.width = 600;
        canvas.height = 400;
        
        // Set style dimensions
        canvas.style.width = '100%';
        canvas.style.height = '100%';
        canvas.style.minWidth = '400px';
        canvas.style.minHeight = '300px';
      }
      
      // Always add a white background to ensure visibility in PDF
      const ctx = canvas.getContext('2d');
      if (ctx) {
        try {
          ctx.save();
          ctx.fillStyle = 'white';
          ctx.fillRect(0, 0, canvas.width, canvas.height);
          ctx.restore();
          
          // Mark this canvas as fixed
          canvas.setAttribute('data-fixed', 'true');
          canvas.setAttribute('data-has-background', 'true');
          
          // Force a redraw of the chart with error handling
          const chartInstance = Chart.getChart(canvas);
          if (chartInstance) {
            chartInstance.update('none'); // Use 'none' mode for better performance
            chartInstanceRef.current = chartInstance;
          }
          

        } catch (error) {
          console.warn('Error initializing chart canvas:', error);
          attemptCount++;
          if (attemptCount < maxAttempts) {
            setTimeout(fixCanvasDimensions, 500 * attemptCount);
          }
        }
      }
    };
    
    // Use requestAnimationFrame for initial attempt
    rafId = requestAnimationFrame(() => {
      fixCanvasDimensions();
    });
    
    // Clean up on unmount
    return () => {
      if (rafId) {
        cancelAnimationFrame(rafId);
      }
      
      // Destroy chart instance if it exists to prevent memory leaks
      if (chartInstanceRef.current) {
        try {
          chartInstanceRef.current.destroy();
          chartInstanceRef.current = null;
        } catch (error) {
          console.warn('Error destroying chart instance:', error);
        }
      }
    };
  }, [data]);

  // Add a function to capture chart as image for PDF export
  const captureChartAsImage = () => {
    if (!renderRef.current) return null;
    
    const canvas = renderRef.current.querySelector('canvas');
    if (canvas && canvas instanceof HTMLCanvasElement && canvas.width > 0 && canvas.height > 0) {
      try {
        // Ensure white background
        const ctx = canvas.getContext('2d');
        if (ctx) {
          // Store current image data
          const imgData = ctx.getImageData(0, 0, canvas.width, canvas.height);
          
          // Add white background
          ctx.save();
          ctx.globalCompositeOperation = 'destination-over';
          ctx.fillStyle = 'white';
          ctx.fillRect(0, 0, canvas.width, canvas.height);
          ctx.restore();
        }
        
        // Get data URL
        return canvas.toDataURL('image/png', 1.0);
      } catch (err) {
        console.error('Error capturing canvas:', err);
      }
    }
    return null;
  };

  // Capture and store chart image when it's ready
  useEffect(() => {
    if (isReady && !renderError && renderRef.current) {
      // Wait for chart to fully render
      const timer = setTimeout(() => {
        const chartImage = captureChartAsImage();
        if (chartImage && renderRef.current) {
          // Store the image as a data attribute for PDF export
          renderRef.current.setAttribute('data-chart-image', chartImage);
        }
      }, 1000);
      
      return () => clearTimeout(timer);
    }
  }, [isReady, renderError]);

  // Hide the chart entirely if performance constraints require it
  if (shouldHide) {
    return null; // Completely hide the chart container
  }

  return (
    <div 
      className={containerClassName} 
      data-type={data?.type || 'unknown'}
      data-chart-title={data?.title || 'Chart'}
      data-ready={isReady ? 'true' : 'false'}
    >
      <div 
        className="chart-container" 
        style={{ 
          height: chartHeight,
          minWidth: '400px',
          minHeight: '300px'
        }}
        ref={renderRef}
        data-chart-type={data?.type || 'unknown'}
      >
        {renderChart()}
      </div>
      {data?.description && !renderError && (
        <div className="chart-description">
          <p style={{ 
            fontSize: halfSize ? '1.1rem' : '1.2rem', 
            lineHeight: '1.6', 
            margin: '16px 0',
            fontStyle: 'italic',
            color: '#444' 
          }}>
            {data.description}
          </p>
        </div>
      )}
    </div>
  );
};

export default VisualizationRenderer; 